import React, { ClipboardEvent, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  CreateProgrammeResponse,
  ViewParentProgrammeRequest,
  ViewSubProgrammeRequest
} from '../../../../api/programme/v2/programme.types';
import { EditorEmailTemplate, Field, RadioOrSelectOption, SubProgrammeField } from '../../../../types/common.types';
import {
  EmailTemplate,
  EmailTemplateProgramme,
  EmailTemplateType,
  Member,
  ParentProgrammeGeneralDetails,
  Programme,
  RegistrationRequirement,
  SubProgrammeGeneralDetails,
  Team,
  TeamMemberExcelFailure,
  TrackingDeviceAssignment
} from '../../../../types/entity.types';

import useOutsideClick from '../../../../hooks/use-outside-click/useOutsideClick';
import useToaster from '../../../../hooks/use-toaster/useToaster';

import {
  ACTIVE,
  ARCHIVED,
  ERROR,
  MEMBERS,
  NEW,
  REQUIRED_REGISTRATION_FIELDS,
  TEAMS,
  UPCOMING
} from '../../../../utils/constants';
import { checkForGeneralChanges } from '../../../../utils/diff.util';
import {
  EMAIL_TYPES,
  PARENT_EMAIL_TARGET,
  createEmailTemplates,
  getTemplatesForTarget,
  mapEmailTemplatesFromVOs,
  mapRequiredRegistrationFieldsFromVO,
  mapRequiredRegistrationFieldsToVO
} from '../../../../utils/general.util';
import { generateUUID, getClassNames, mapStringOptionsToObjectOptions } from '../../../../utils/util';

import ProgrammeLayout from '../../../../layouts/programme/ProgrammeLayout';
import Button from '../../../common/button/Button';
import Checkbox from '../../../common/input/checkbox/Checkbox';
import DatePicker from '../../../common/input/date-picker/DatePicker';
import Dropdown from '../../../common/input/dropdown/Dropdown';
import TextArea from '../../../common/input/text-area/TextArea';
import TextInput from '../../../common/input/text-input/TextInput';
import Modal from '../../../common/modal/Modal';
import SaveChangesModal from '../../../common/modal/save-changes-modal/SaveChangesModal';
import RichTextEditor from '../../../common/rich-text-editor/RichTextEditor';
import MemberTable from './member-table/MemberTable';
import TeamTable from './team-table/TeamTable';

import { ReactComponent as BackIcon } from '../../../../resources/icons/back.svg';
import { ReactComponent as CloseIcon } from '../../../../resources/icons/close.svg';
import { ReactComponent as GearIcon } from '../../../../resources/icons/gear.svg';
import { ReactComponent as SaveIcon } from '../../../../resources/icons/save.svg';

import './general.scss';

const THEMES = ['The Healthy Whole', 'PPS'];

type Props = {
  programme?: Programme;
  parentProgramme?: ParentProgrammeGeneralDetails;
  subProgramme?: SubProgrammeGeneralDetails;
  members: Member[];
  team?: Team;
  teams: Team[];
  isProgrammeComplete?: boolean;
  createProgramme: (
    programmeToSave: Programme,
    emailTemplates: Record<string, EditorEmailTemplate[]>,
    onSuccess: (data: CreateProgrammeResponse) => void
  ) => void;
  updateProgramme: (
    programme: Programme,
    emailTemplates: Record<string, EditorEmailTemplate[]>,
    onSuccess: () => void,
    changedParts?: string[]
  ) => void;
  viewParentProgramme: (
    requestData: ViewParentProgrammeRequest,
    onSuccess?: (data: ParentProgrammeGeneralDetails) => void
  ) => void;
  viewSubProgramme: (requestData: ViewSubProgrammeRequest) => void;
  createMember: (memberToSave: Member, onSuccess: () => void) => void;
  updateMember: (memberToSave: Member, close?: () => void) => void;
  deleteMember: (externalReference: string, onSuccess: () => void) => void;
  downloadMemberExcel: () => void;
  uploadMemberExcel: (file: File) => void;
  activateProgramme: () => void;
  archiveProgramme: () => void;
  createTeam: (team: Team, onSuccess: () => void) => void;
  updateTeam: (team: Team, onSuccess: () => void) => void;
  deleteTeam: (onSuccess: () => void) => void;
  retrieveMembers: (externalReference: string) => void;
  createTrackingDeviceAssignment: (
    systemUserExternalReference: string,
    deviceId: string,
    onSuccess?: (data: TrackingDeviceAssignment) => void
  ) => void;
  retrieveTrackingDeviceAssignments: (
    systemUserExternalReference: string,
    onSuccess?: (data: TrackingDeviceAssignment[]) => void
  ) => void;
  addMemberToTeam: (memberExternalReference: string, close: () => void) => void;
  removeMemberFromTeam: (memberExternalReference: string, onSuccess: () => void) => void;
  uploadTeamExcel: (file: File, onError: (value: TeamMemberExcelFailure[]) => void) => void;
  downloadTeamExcel: () => void;
  sendMemberActivationCommunications: (externalReference: string, sendSMS: boolean, sendEmail: boolean) => void;
  retrieveEmailTemplateByType: (
    programmeExternalReference: string,
    type: EmailTemplateType,
    onSuccess: (data: EmailTemplate[]) => void
  ) => void;
  sendTestEmail: (template: EditorEmailTemplate, emailAddress: string, programme: EmailTemplateProgramme) => void;
};

const General = ({
  programme,
  parentProgramme,
  subProgramme,
  members,
  team,
  teams,
  isProgrammeComplete = false,
  createProgramme,
  updateProgramme,
  viewParentProgramme,
  viewSubProgramme,
  createMember,
  updateMember,
  deleteMember,
  downloadMemberExcel,
  uploadMemberExcel,
  activateProgramme,
  archiveProgramme,
  createTeam,
  updateTeam,
  deleteTeam,
  retrieveMembers,
  createTrackingDeviceAssignment,
  retrieveTrackingDeviceAssignments,
  addMemberToTeam,
  removeMemberFromTeam,
  uploadTeamExcel,
  downloadTeamExcel,
  sendMemberActivationCommunications,
  retrieveEmailTemplateByType,
  sendTestEmail
}: Props) => {
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string>();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isCreatingSubProgrammes, setIsCreatingSubProgrammes] = useState(false);
  const [hasRegistrationRequirements, setHasRegistrationRequirements] = useState(false);
  const [unsavedSubProgramme, setUnsavedSubProgramme] = useState<string>();

  const [programmeName, setProgrammeName] = useState<string | undefined>('');
  const [programmeDescription, setProgrammeDescription] = useState<string | undefined>('');
  const [programmeFromDate, setProgrammeFromDate] = useState<Date | null>(null);
  const [programmeToDate, setProgrammeToDate] = useState<Date | null>(null);
  const [programmeTheme, setProgrammeTheme] = useState<string>();
  const [programmeIsNutrition, setProgrammeIsNutrition] = useState(true);
  const [programmeIsMovement, setProgrammeIsMovement] = useState(true);
  const [programmeIsMeasurements, setProgrammeIsMeasurements] = useState(true);
  const [programmeIsSleep, setProgrammeIsSleep] = useState(true);
  const [programmeIsCondition, setProgrammeIsCondition] = useState(true);
  const [programmeIsSelfCare, setProgrammeIsSelfCare] = useState(true);
  const [programmeIsSession, setProgrammeIsSession] = useState(true);
  const [programmeIsCharity, setProgrammeIsCharity] = useState(true);
  const [programmePrice, setProgrammePrice] = useState('');
  const [programmeFields, setProgrammeFields] = useState<Field[] | string[]>([]);
  const [programmeLeaderboardCollectFrom, setProgrammeLeaderboardCollectFrom] = useState<Date | null>(null);

  const [payfastMerchantId, setPayfastMerchantId] = useState('');
  const [payfastMerchantKey, setPayfastMerchantKey] = useState('');

  const [subProgrammes, setSubProgrammes] = useState<SubProgrammeField[]>([]);
  const [subProgrammeName, setSubProgrammeName] = useState<string | undefined>('');

  const [emailTarget, setEmailTarget] = useState(PARENT_EMAIL_TARGET);
  const [emailType, setEmailType] = useState(EMAIL_TYPES[0]!);
  const [emailTemplates, setEmailTemplates] = useState<Record<string, EditorEmailTemplate[]>>({});
  const [showEmailLegend, setShowEmailLegend] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const scrollRef = useRef<HTMLInputElement | null>(null);
  const changedPartsRef = useRef<string[] | null>(null);
  const emailLegendRef = useRef<HTMLDivElement | null>(null);
  const emailLegendButtonRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();

  const { showToaster } = useToaster();

  const { view, programmeExternalReference, status } = useParams();

  const isNewProgramme = status === NEW;
  const isUpcomingProgramme = status === UPCOMING;
  const isArchivedProgramme = status === ARCHIVED;
  const showsSettingsInline = isNewProgramme || isUpcomingProgramme;
  const requiresSettingsToggle = !showsSettingsInline;

  useEffect(() => {
    if (!programme?.externalReference) return;

    setProgrammeName(programme.name);
    setProgrammeDescription(programme.description);

    if (programme.fromDate) {
      setProgrammeFromDate(new Date(programme.fromDate));
    }
    if (programme.toDate) {
      setProgrammeToDate(new Date(programme.toDate));
    }

    setProgrammeTheme(programme.theme);
    setProgrammeIsNutrition(programme.isNutrition ?? false);
    setProgrammeIsMovement(programme.isMovement ?? false);
    setProgrammeIsMeasurements(programme.isMeasurements ?? false);
    setProgrammeIsSleep(programme.isSleep ?? false);
    setProgrammeIsCondition(programme.isCondition ?? false);
    setProgrammeIsSelfCare(programme.isSelfCare ?? false);
    setProgrammeIsSession(programme.isSession ?? false);
    setProgrammeIsCharity(programme.charityWork ?? false);

    if (programme.leaderboardCollectFrom) {
      setProgrammeLeaderboardCollectFrom(new Date(programme.leaderboardCollectFrom));
    }

    setHasRegistrationRequirements(programme.hasRegistrationRequirements ?? false);

    const { registrationRequirement } = programme;
    if (registrationRequirement) {
      setPayfastMerchantId(registrationRequirement.payfastMerchantId ?? '');
      setPayfastMerchantKey(registrationRequirement.payfastMerchantKey ?? '');
      setProgrammePrice(registrationRequirement.programmePrice);
      setProgrammeFields(mapRequiredRegistrationFieldsFromVO(registrationRequirement));
    }

    setRedirectUrl(undefined);

    if (status === NEW) return;

    retrieveEmailTemplateByType(programme.externalReference, emailType.value as EmailTemplateType, (data) => {
      const mappedTemplates = mapEmailTemplatesFromVOs(data);

      setEmailTemplates((templates) => ({
        ...templates,
        [programme.externalReference!]: mappedTemplates.length > 0 ? mappedTemplates : createEmailTemplates()
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, programme]);

  useEffect(() => {
    if (!parentProgramme || !subProgramme) return;

    setProgrammeName(parentProgramme.programmeGroupName);
    if (parentProgramme.runningFromDate) {
      setProgrammeFromDate(new Date(parentProgramme.runningFromDate));
    }
    if (parentProgramme.runningToDate) {
      setProgrammeToDate(new Date(parentProgramme.runningToDate));
    }

    setSubProgrammeName(subProgramme.name);
    setProgrammeDescription(subProgramme.description);
    setProgrammeTheme(subProgramme.theme);
    setProgrammeIsNutrition(subProgramme.nutritionEnabled ?? false);
    setProgrammeIsMovement(subProgramme.movementEnabled ?? false);
    setProgrammeIsMeasurements(subProgramme.measurementEnabled ?? false);
    setProgrammeIsSleep(subProgramme.sleepEnabled ?? false);
    setProgrammeIsCondition(subProgramme.conditionEnabled ?? false);
    setProgrammeIsSelfCare(subProgramme.selfCareEnabled ?? false);
    setProgrammeIsSession(subProgramme.sessionEnabled ?? false);
    setProgrammeIsCharity(subProgramme.charityWorkEnabled ?? false);

    if (subProgramme.leaderboardCollectFrom) {
      setProgrammeLeaderboardCollectFrom(new Date(subProgramme.leaderboardCollectFrom));
    }

    setHasRegistrationRequirements(subProgramme.hasRegistrationRequirements ?? false);

    if (subProgramme.hasRegistrationRequirements) {
      setPayfastMerchantId(subProgramme.payfastMerchantId ?? '');
      setPayfastMerchantKey(subProgramme.payfastMerchantKey ?? '');
      setProgrammePrice(subProgramme.programmePrice ?? '');
      setProgrammeFields(mapRequiredRegistrationFieldsFromVO(subProgramme));
    }

    setRedirectUrl(undefined);

    if (status === NEW) return;

    retrieveEmailTemplateByType(subProgramme.externalReference!, emailType.value as EmailTemplateType, (data) => {
      const mappedTemplates = mapEmailTemplatesFromVOs(data);
      setEmailTemplates({
        [subProgramme.externalReference!]: mappedTemplates.length > 0 ? mappedTemplates : createEmailTemplates()
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, parentProgramme, subProgramme]);

  useEffect(() => {
    if (programme?.externalReference) {
      setEmailTarget(programme.externalReference ?? PARENT_EMAIL_TARGET);
    }

    if (parentProgramme && subProgramme) {
      setEmailTarget(subProgramme.externalReference ?? PARENT_EMAIL_TARGET);
    }
  }, [status, programme, parentProgramme, subProgramme]);

  useEffect(() => {
    setIsSettingsOpen(false);
  }, [programmeExternalReference]);

  useEffect(() => {
    if (status === NEW) {
      setProgrammeName('');
      setProgrammeDescription('');
      setProgrammeFromDate(null);
      setProgrammeToDate(null);
      setProgrammeTheme(undefined);
      setProgrammeIsNutrition(true);
      setProgrammeIsMovement(true);
      setProgrammeIsSleep(true);
      setProgrammeIsCondition(true);
      setProgrammeIsSelfCare(true);
      setProgrammeIsSession(true);
      setProgrammeIsCharity(true);
      setProgrammeIsMeasurements(true);
      setProgrammeLeaderboardCollectFrom(null);
      setHasRegistrationRequirements(false);
      setPayfastMerchantId('');
      setPayfastMerchantKey('');
      setProgrammePrice('');
      setProgrammeFields([]);
      setEmailTarget(PARENT_EMAIL_TARGET);
      setEmailType(EMAIL_TYPES[0]!);
      setEmailTemplates({
        [PARENT_EMAIL_TARGET]: createEmailTemplates()
      });
    }

    setSubProgrammes([{ id: generateUUID(), name: '', price: '' }]);
    setIsCreatingSubProgrammes(false);
  }, [status]);

  const subProgrammeOptions = useMemo(() => {
    if (status === NEW || !parentProgramme?.subProgrammeSummaries) return [];

    return parentProgramme.subProgrammeSummaries.map((subProgramme) => ({
      label: subProgramme.name,
      value: subProgramme.externalReference
    }));
  }, [parentProgramme, status]);

  useOutsideClick([emailLegendRef, emailLegendButtonRef], () => setShowEmailLegend(false), showEmailLegend);

  const handleSaveClick = (changedParts: string[]) => {
    if (!programmeFromDate) {
      showToaster(ERROR, 'Please provide a from Date.');
      return;
    }

    if (!programmeFromDate) {
      showToaster(ERROR, 'Please provide a from Date.');
      return;
    }

    if (!programmeToDate) {
      showToaster(ERROR, 'Please provide a to Date.');
      return;
    }

    if (!programmeTheme) {
      showToaster(ERROR, 'Please provide a Programme Theme.');
      return;
    }

    const programmeToSave: Programme = {
      name: programmeName!,
      description: programmeDescription!,
      fromDate: programmeFromDate,
      toDate: programmeToDate,
      theme: programmeTheme,
      isNutrition: programmeIsNutrition,
      isMovement: programmeIsMovement,
      isMeasurements: programmeIsMeasurements,
      isSleep: programmeIsSleep,
      isCondition: programmeIsCondition,
      isSelfCare: programmeIsSelfCare,
      isSession: programmeIsSession,
      charityWork: programmeIsCharity,
      status: status === NEW ? UPCOMING : status,
      hasRegistrationRequirements,
      leaderboardCollectFrom: programmeLeaderboardCollectFrom ?? null,
      registrationRequirement: hasRegistrationRequirements
        ? {
            requiredRegistrationFields: mapRequiredRegistrationFieldsToVO(programmeFields as Field[]),
            payfastMerchantId,
            payfastMerchantKey,
            programmePrice
          }
        : null,
      subProgrammes: isCreatingSubProgrammes ? subProgrammes : [],
      subProgrammeName: subProgrammeName
    };

    if (programme?.externalReference || parentProgramme?.parentProgrammeExternalReference) {
      updateProgramme(
        programmeToSave,
        emailTemplates,
        () => {
          if (redirectUrl) {
            setRedirectUrl(undefined);
            navigate(redirectUrl);
          } else if (unsavedSubProgramme) {
            viewParentProgramme({ programmeExternalReference: programmeExternalReference! });
            viewSubProgramme({
              programmeExternalReference: programmeExternalReference!,
              subProgrammeExternalReference: unsavedSubProgramme
            });
            setUnsavedSubProgramme(undefined);
          } else if (parentProgramme && subProgramme) {
            viewParentProgramme({ programmeExternalReference: parentProgramme.parentProgrammeExternalReference });
            viewSubProgramme({
              programmeExternalReference: parentProgramme.parentProgrammeExternalReference,
              subProgrammeExternalReference: subProgramme.externalReference!
            });
          }
        },
        changedParts
      );
      return;
    }

    createProgramme(programmeToSave, emailTemplates, (data) => {
      if (redirectUrl) {
        setRedirectUrl(undefined);
        navigate(redirectUrl);
      } else {
        const hasSubProgrammes = programmeToSave.subProgrammes && programmeToSave.subProgrammes.length > 0;
        navigate(
          `/programmes/upcoming/${data.data.programmeExternalReference}/general/members?hasSubProgrammes=${hasSubProgrammes}`
        );
      }
    });
  };

  const handleArchiveConfirmationClick = () => {
    archiveProgramme();
    setShowArchiveModal(false);
  };

  const handleAddRequiredFieldClick = () => {
    if (!programmeFields) {
      return;
    }

    const fields: Field[] = [
      {
        label: '',
        optional: false,
        type: 'text'
      }
    ];

    setProgrammeFields([...(programmeFields as Field[]), ...fields]);
    if (status !== UPCOMING) {
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };

  const handleRequiredFieldLabelChange = (value: string, index: number) => {
    const fields = [...programmeFields] as Field[];
    if (!fields[index]) {
      return;
    }
    fields[index].label = value;
    setProgrammeFields(fields);
  };

  const handleRequiredFieldOptionalChange = (value: boolean, index: number) => {
    const fields = [...programmeFields] as Field[];
    if (!fields[index]) {
      return;
    }
    fields[index].optional = value;
    setProgrammeFields(fields);
  };

  const handleRequiredFieldCheckboxChange = (value: boolean, index: number) => {
    const fields = [...programmeFields] as Field[];
    if (!fields[index]) {
      return;
    }
    fields[index].type = value ? 'checkbox' : 'text';
    setProgrammeFields(fields);
  };

  const handleDeleteRequiredFieldClick = (index: number) => {
    const fields = [...programmeFields] as Field[];
    fields.splice(index, 1);
    setProgrammeFields(fields);
  };

  const checkForUnsavedChanges = (url?: string, subProgrammeExternalReference?: string) => {
    let registrationRequirement: RegistrationRequirement | null = null;
    if (hasRegistrationRequirements) {
      const requiredRegistrationFields = mapRequiredRegistrationFieldsToVO(programmeFields as Field[]);

      registrationRequirement = {
        requiredRegistrationFields,
        payfastMerchantId,
        payfastMerchantKey,
        programmePrice
      };
    }

    const programmeToSave: Programme = {
      ...programme,
      name: programmeName!,
      description: programmeDescription!,
      fromDate: programmeFromDate,
      toDate: programmeToDate,
      theme: programmeTheme,
      isNutrition: programmeIsNutrition,
      isMovement: programmeIsMovement,
      isMeasurements: programmeIsMeasurements,
      isSleep: programmeIsSleep,
      isCondition: programmeIsCondition,
      isSelfCare: programmeIsSelfCare,
      isSession: programmeIsSession,
      charityWork: programmeIsCharity,
      leaderboardCollectFrom: programmeLeaderboardCollectFrom,
      subProgrammeName: subProgrammeName,
      subProgrammes,
      hasRegistrationRequirements,
      registrationRequirement
    };

    const unsavedChanges = checkForGeneralChanges(programme, programmeToSave, parentProgramme, subProgramme);
    const hasUnsavedChanges = unsavedChanges.length > 0;

    if (url) {
      if (hasUnsavedChanges) {
        changedPartsRef.current = unsavedChanges;
        setRedirectUrl(url);
        return;
      } else {
        navigate(url);
      }
    } else if (subProgrammeExternalReference) {
      if (hasUnsavedChanges) {
        changedPartsRef.current = unsavedChanges;
        setUnsavedSubProgramme(subProgrammeExternalReference);
      } else {
        viewSubProgramme({
          programmeExternalReference: programmeExternalReference!,
          subProgrammeExternalReference: subProgrammeExternalReference
        });
        retrieveMembers(subProgrammeExternalReference);
        retrieveEmailTemplateByType(subProgrammeExternalReference, emailType.value as EmailTemplateType, (data) => {
          const mappedTemplates = mapEmailTemplatesFromVOs(data);

          setEmailTemplates({
            [subProgrammeExternalReference]: mappedTemplates.length > 0 ? mappedTemplates : createEmailTemplates()
          });
        });
      }
    }
  };

  const handleToggleClick = (newView: string) => {
    if (newView === view) return;

    navigate(
      `/programmes/${status}/${programmeExternalReference}/general/${newView}?hasSubProgrammes=${hasSubProgramme}`
    );
  };

  const handleSubProgrammeChange = (value: string, id: string) => {
    const updatedSubProgrammes = subProgrammes.map((subProgramme) => {
      if (subProgramme.id === id) {
        return {
          ...subProgramme,
          name: value
        };
      }

      return subProgramme;
    });

    setSubProgrammes(updatedSubProgrammes);
  };

  const handleAddSubProgrammeClick = () => {
    const newSubProgramme = { id: generateUUID(), name: '', price: '' };

    setSubProgrammes([...subProgrammes, newSubProgramme]);
    setEmailTemplates((templatesByTarget) => ({
      ...templatesByTarget,
      [newSubProgramme.id]: createEmailTemplates()
    }));
  };

  const handleDeleteSubProgrammeClick = (id: string) => {
    setSubProgrammes(subProgrammes.filter((subProgramme) => subProgramme.id !== id));
    setEmailTemplates((templatesByTarget) =>
      Object.fromEntries(Object.entries(templatesByTarget).filter(([target]) => target !== id))
    );

    if (emailTarget === id) {
      setEmailTarget(PARENT_EMAIL_TARGET);
    }
  };

  const handleSubProgrammePriceChange = (value: string, subProgrammeId: string) => {
    const updatedSubProgrammes = subProgrammes.map((subProgramme) => {
      if (subProgramme.id === subProgrammeId) {
        return {
          ...subProgramme,
          price: value
        };
      }

      return subProgramme;
    });

    setSubProgrammes(updatedSubProgrammes);
  };

  const handleSubProgrammeDropdownChange = (option: RadioOrSelectOption) => {
    if (option.value === subProgramme?.externalReference) return;

    checkForUnsavedChanges(undefined, option.value);
  };

  const handleEmailSubjectChange = (value: string) => {
    setEmailTemplates((templatesByTarget) => ({
      ...templatesByTarget,
      [emailTarget]: getTemplatesForTarget(templatesByTarget, emailTarget).map((template) =>
        template.type === emailType.value ? { ...template, subject: value } : template
      )
    }));
  };

  const handleEmailBodyChange = (value: SetStateAction<object | undefined>) => {
    setEmailTemplates((templatesByTarget) => ({
      ...templatesByTarget,
      [emailTarget]: getTemplatesForTarget(templatesByTarget, emailTarget).map((template) => {
        if (template.type !== emailType.value) return template;

        const body =
          typeof value === 'function'
            ? (value as (prevState: object | undefined) => object | undefined)(template.body)
            : value;

        return { ...template, body };
      })
    }));
  };

  const handleDiscardUnsavedChangesClick = () => {
    if (redirectUrl) {
      navigate(redirectUrl);
    } else if (unsavedSubProgramme) {
      viewSubProgramme({
        programmeExternalReference: programmeExternalReference!,
        subProgrammeExternalReference: unsavedSubProgramme
      });
      setUnsavedSubProgramme(undefined);
    }
  };

  const renderToggle = (upcoming: boolean) => {
    return (
      <div className={getClassNames('general__toggle', { 'sub-programme': hasSubProgramme, upcoming, team })}>
        <button
          className={getClassNames('general__toggle-button', { active: view === MEMBERS })}
          onClick={() => handleToggleClick(MEMBERS)}
        >
          Members
        </button>
        <button
          className={getClassNames('general__toggle-button', { active: view === TEAMS })}
          onClick={() => handleToggleClick(TEAMS)}
        >
          Teams
        </button>

        {team && (
          <button className="general__toggle-back-button" onClick={() => navigate(-1)}>
            <BackIcon className="general__toggle-back-button__icon" />
            <p>Back</p>
          </button>
        )}
      </div>
    );
  };

  const renderTable = () => {
    if (view === TEAMS) {
      if (teams) {
        return (
          <TeamTable
            team={team}
            teams={teams}
            members={members}
            createTeam={createTeam}
            updateTeam={updateTeam}
            deleteTeam={deleteTeam}
            addMemberToTeam={addMemberToTeam}
            removeMemberFromTeam={removeMemberFromTeam}
            uploadTeamExcel={uploadTeamExcel}
            downloadTeamExcel={downloadTeamExcel}
          />
        );
      }
    }

    return (
      <MemberTable
        members={members}
        createMember={createMember}
        updateMember={updateMember}
        deleteMember={deleteMember}
        uploadMemberExcel={uploadMemberExcel}
        downloadMemberExcel={downloadMemberExcel}
        sendMemberActivationCommunications={sendMemberActivationCommunications}
        createTrackingDeviceAssignment={createTrackingDeviceAssignment}
        retrieveTrackingDeviceAssignments={retrieveTrackingDeviceAssignments}
      />
    );
  };

  const hasSubProgramme = !!(parentProgramme && subProgramme);

  const renderSubProgrammeDropdown = () => (
    <Dropdown
      name="subProgramme"
      value={subProgramme ? { label: subProgramme.name, value: subProgramme.externalReference! } : undefined}
      options={subProgrammeOptions}
      onChange={handleSubProgrammeDropdownChange}
    />
  );

  const emailTargetOptions: RadioOrSelectOption[] = useMemo(
    () => [
      { label: programmeName || 'Parent Programme', value: PARENT_EMAIL_TARGET },
      ...subProgrammes.map((subProgramme, index) => ({
        label: subProgramme.name || `Sub-Programme ${index + 1}`,
        value: subProgramme.id
      }))
    ],
    [programmeName, subProgrammes]
  );

  const selectedEmailTemplate = getTemplatesForTarget(emailTemplates, emailTarget).find(
    (template) => template.type === emailType.value
  );

  const handleEmailTemplateTypeChange = (option: RadioOrSelectOption) => {
    setEmailType(option);

    if (isNewProgramme) return;

    const isEmailTemplateLoaded = emailTemplates[emailTarget]?.some(
      (template) => template.type === option.value && template.externalReference
    );
    if (isEmailTemplateLoaded) return;

    retrieveEmailTemplateByType(emailTarget, option.value as EmailTemplateType, (data) => {
      setEmailTemplates((templates) => ({
        ...templates,
        [emailTarget]: mapEmailTemplatesFromVOs(data, emailTemplates[emailTarget])
      }));
    });
  };

  const handleSendExampleEmailClick = () => {
    if (!selectedEmailTemplate) return;

    const newSubProgramme = subProgrammes.find((subProgramme) => subProgramme.id === emailTarget);
    const isSubProgrammeTarget = hasSubProgramme || !!newSubProgramme;
    const resolvedSubProgrammeName = subProgrammeName || newSubProgramme?.name;

    const templateProgramme: EmailTemplateProgramme = {
      programmeName: isSubProgrammeTarget ? undefined : programmeName || undefined,
      subProgrammeName: isSubProgrammeTarget ? resolvedSubProgrammeName || undefined : undefined,
      startDate: programmeFromDate?.toISOString(),
      endDate: programmeToDate?.toISOString()
    };

    sendTestEmail(selectedEmailTemplate, testEmail, templateProgramme);
  };

  const handleLegendCopy = (event: ClipboardEvent<HTMLDivElement>) => {
    const selection = window.getSelection()?.toString();
    if (!selection) return;

    event.preventDefault();
    event.clipboardData.setData('text/plain', selection);
  };

  const renderEmailEditor = () => (
    <div className="general__email-editor">
      <div className="general__email-editor__header">
        <h1>Welcome Email</h1>
        <div ref={emailLegendButtonRef}>
          <Button text="{} LEGEND" onClick={() => setShowEmailLegend((prev) => !prev)} />
        </div>

        {showEmailLegend && (
          <div className="general__email-editor__legend" ref={emailLegendRef} onCopy={handleLegendCopy}>
            <p>LEGEND</p>
            <div>
              <p>{'{USERNAME}'}</p>
              <p>Member Username</p>
              <p>{'{NAME}'}</p>
              <p>Member Name</p>
              <p>{'{SURNAME}'}</p>
              <p>Member Last Name</p>
              <p>{'{PROGRAMME}'}</p>
              <p>Parent Programme Name</p>
              <p>{'{SUB-PROGRAMME}'}</p>
              <p>Sub-Programme Name</p>
              <p>{'{START DATE}'}</p>
              <p>Programme Start Date</p>
              <p>{'{END DATE}'}</p>
              <p>Programme End Date</p>
              <p>{'{IOS LINK}'}</p>
              <p>Link to iOS App Store</p>
              <p>{'{ANDROID LINK}'}</p>
              <p>Link to Android App Store</p>
              <p>{'{HUAWEI LINK}'}</p>
              <p>Link to HUAWEI App Store</p>
              <p>{'{REGISTER BUTTON}'}</p>
              <p>Button to registration page</p>
              <p>{'{REGISTER LINK}'}</p>
              <p>Link to registration page</p>
              <p>{'{SUPPORT EMAIL}'}</p>
              <p>Support Person's Email Address</p>
            </div>
          </div>
        )}
      </div>

      {isCreatingSubProgrammes && (
        <Dropdown
          value={emailTargetOptions.find((option) => option.value === emailTarget)}
          options={emailTargetOptions}
          onChange={(option) => setEmailTarget(option.value)}
        />
      )}

      <Dropdown value={emailType} options={EMAIL_TYPES} onChange={handleEmailTemplateTypeChange} />

      <TextInput
        label="Subject:"
        name="subject"
        value={selectedEmailTemplate?.subject ?? ''}
        onChange={({ target }) => handleEmailSubjectChange(target.value)}
      />

      <div className="general__email-editor__rich-text-editor">
        <RichTextEditor editorState={selectedEmailTemplate?.body} setEditorState={handleEmailBodyChange} />
      </div>

      <div className="general__email-editor__test-email">
        <TextInput
          name="testEmail"
          placeholder="Enter email address here"
          value={testEmail}
          onChange={({ target }) => setTestEmail(target.value)}
        />
        <Button text="Send example email" onClick={handleSendExampleEmailClick} disabled={!testEmail} />
      </div>
    </div>
  );

  const renderDescriptionField = () => (
    <TextArea
      label="Description:"
      name="description"
      value={programmeDescription ?? ''}
      onChange={({ target }) => setProgrammeDescription(target.value)}
      disabled={isArchivedProgramme}
    />
  );

  const renderThemeField = () => (
    <Dropdown
      label="Theme:"
      name="theme"
      options={mapStringOptionsToObjectOptions(THEMES)}
      value={{ label: programmeTheme ?? '', value: programmeTheme ?? '' }}
      onChange={(option) => setProgrammeTheme(option.value)}
      disabled={isArchivedProgramme}
    />
  );

  return (
    <ProgrammeLayout onNavigate={checkForUnsavedChanges}>
      <div className="general" data-testid={`general-${status}`}>
        <div className="general__icons">
          {((isSettingsOpen && !isArchivedProgramme) || showsSettingsInline) && (
            <div className="general__icon-container" onClick={() => handleSaveClick(hasSubProgramme ? ['parent'] : [])}>
              <SaveIcon className="general__save-icon" data-testid="save-icon" />
            </div>
          )}
          {!isSettingsOpen && requiresSettingsToggle && (
            <div className="general__icon-container" onClick={() => setIsSettingsOpen(true)}>
              <GearIcon className="general__gear-icon" data-testid="cog-icon" />
            </div>
          )}
          {isSettingsOpen && requiresSettingsToggle && (
            <div className="general__icon-container" onClick={() => setIsSettingsOpen(false)}>
              <CloseIcon className="general__close-icon" data-testid="close-icon" />
            </div>
          )}
        </div>

        <div className="general__scroll-container">
          {status === ACTIVE && isSettingsOpen && (
            <Button text="Archive" onClick={() => setShowArchiveModal(true)} disabled={isProgrammeComplete} />
          )}
          {requiresSettingsToggle && (
            <div className="general__header-container">
              {!isSettingsOpen && (
                <div className="">
                  {hasSubProgramme && (
                    <div className="general__input-container__sub-programme-dropdown general__input-container__sub-programme-dropdown--main">
                      {renderSubProgrammeDropdown()}
                    </div>
                  )}
                  {renderToggle(false)}
                </div>
              )}

              {isSettingsOpen && <h1>Settings</h1>}
            </div>
          )}
          {isUpcomingProgramme && <Button text="Publish" onClick={activateProgramme} disabled={!isProgrammeComplete} />}

          {showsSettingsInline && (
            <div className={getClassNames('general__header-container', { new: isNewProgramme })}>
              <h1>{hasSubProgramme ? 'Parent Programme Settings' : 'Settings'}</h1>
            </div>
          )}
          {(showsSettingsInline || isSettingsOpen) && (
            <div className={getClassNames('general__input-container', { 'sub-programme-short': hasSubProgramme })}>
              <TextInput
                label="Programme Name:"
                name="programme"
                value={programmeName ?? ''}
                onChange={({ target }) => setProgrammeName(target.value)}
                disabled={isArchivedProgramme}
              />
              {!hasSubProgramme && renderDescriptionField()}
              <div className="general__input-container__datepicker-container">
                <DatePicker
                  label="Running from:"
                  name="running-from"
                  value={programmeFromDate}
                  onChange={(date) => {
                    if (!programmeLeaderboardCollectFrom || programmeLeaderboardCollectFrom === programmeFromDate) {
                      setProgrammeLeaderboardCollectFrom(date);
                    }

                    setProgrammeFromDate(date);
                  }}
                  disabled={isArchivedProgramme}
                  maxDate={programmeToDate ?? undefined}
                />
                <DatePicker
                  label="To:"
                  name="running-to"
                  value={programmeToDate}
                  onChange={(date) => setProgrammeToDate(date)}
                  disabled={isArchivedProgramme}
                  minDate={programmeFromDate ?? undefined}
                />
              </div>
              {!hasSubProgramme && renderThemeField()}

              {isNewProgramme && (
                <div className="general__header-container">
                  <h1>Create Sub-Programmes</h1>
                  <div className="general__input-container__checkbox">
                    <Checkbox
                      name="sub-programmes"
                      value={isCreatingSubProgrammes ?? false}
                      onChange={setIsCreatingSubProgrammes}
                    />
                  </div>
                </div>
              )}

              {hasSubProgramme && (
                <>
                  <div className="general__header-container">
                    <h1>Sub-Programme Settings</h1>
                  </div>

                  <div className={getClassNames('general__input-container', { 'sub-programme': true })}>
                    <div className="general__input-container__sub-programme-dropdown">
                      {renderSubProgrammeDropdown()}
                      <Button
                        text="Save Changes"
                        onClick={() => handleSaveClick(['sub'])}
                        disabled={isArchivedProgramme}
                      />
                    </div>
                    <TextInput
                      label="Sub-Programme Name:"
                      name="subProgrammeName"
                      value={subProgrammeName ?? ''}
                      onChange={({ target }) => setSubProgrammeName(target.value)}
                      disabled={isArchivedProgramme}
                    />
                    {renderThemeField()}
                    {renderDescriptionField()}
                  </div>
                </>
              )}

              {isCreatingSubProgrammes && (
                <div className="general__sub-programmes">
                  {subProgrammes.map((subProgramme, index) => (
                    <div key={subProgramme.id} className="general__sub-programmes__sub-programme">
                      <label htmlFor={subProgramme.id}>{index + 1}:</label>
                      <TextInput
                        name={subProgramme.id}
                        value={subProgramme.name}
                        onChange={({ target }) => handleSubProgrammeChange(target.value, subProgramme.id)}
                      />
                      {subProgrammes.length > 1 && (
                        <button
                          className="general__sub-programmes__sub-programme__delete-button"
                          onClick={() => handleDeleteSubProgrammeClick(subProgramme.id)}
                        >
                          <CloseIcon />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button
                    className="general__sub-programmes__add-button"
                    text="Add"
                    onClick={handleAddSubProgrammeClick}
                    disabled={subProgrammes.some((subProgramme) => !subProgramme.name)}
                  />
                </div>
              )}

              <div className="general__header-container">
                <h1>Active Features</h1>
              </div>

              <div className="general__input-container__checkbox-container">
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Nutrition:"
                    name="is-nutrition"
                    value={programmeIsNutrition ?? false}
                    onChange={(value) => setProgrammeIsNutrition(value)}
                    disabled={isArchivedProgramme}
                  />
                </div>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Movement:"
                    name="is-movement"
                    value={programmeIsMovement ?? false}
                    onChange={(value) => setProgrammeIsMovement(value)}
                    disabled={isArchivedProgramme}
                  />
                </div>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Measurements:"
                    name="is-measurements"
                    value={programmeIsMeasurements ?? false}
                    onChange={(value) => setProgrammeIsMeasurements(value)}
                    disabled={isArchivedProgramme}
                  />
                </div>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Self-care Sleep:"
                    name="is-sleep"
                    value={programmeIsSleep ?? false}
                    onChange={(value) => setProgrammeIsSleep(value)}
                    disabled={isArchivedProgramme}
                  />
                </div>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Condition:"
                    name="is-condition"
                    value={programmeIsCondition ?? false}
                    onChange={(value) => setProgrammeIsCondition(value)}
                    disabled={isArchivedProgramme}
                  />
                </div>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Self Care:"
                    name="is-self-care"
                    value={programmeIsSelfCare ?? false}
                    onChange={(value) => setProgrammeIsSelfCare(value)}
                    disabled={isArchivedProgramme}
                  />
                </div>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Session:"
                    name="is-session"
                    value={programmeIsSession ?? false}
                    onChange={(value) => setProgrammeIsSession(value)}
                    disabled={isArchivedProgramme}
                  />
                </div>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Charity Work:"
                    name="is-charity"
                    value={programmeIsCharity ?? false}
                    onChange={(value) => setProgrammeIsCharity(value)}
                    disabled={isArchivedProgramme}
                  />
                </div>
              </div>

              <div className="general__header-container">
                <h1>Leaderboard Settings</h1>
              </div>

              <div className="general__input-container__leaderboard-settings">
                <div className="general__input-container__datepicker-container">
                  <DatePicker
                    label="Collect data from:"
                    name="collect-data-from"
                    value={
                      (programmeLeaderboardCollectFrom ? programmeLeaderboardCollectFrom : programmeFromDate) as Date
                    }
                    onChange={(date) => setProgrammeLeaderboardCollectFrom(date)}
                    disabled={isArchivedProgramme}
                  />
                </div>
              </div>

              <div className="general__header-container">
                <h1>Registration Requirements</h1>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    name="registration-requirements"
                    value={hasRegistrationRequirements ?? false}
                    onChange={setHasRegistrationRequirements}
                    disabled={isArchivedProgramme}
                  />
                </div>
              </div>

              {hasRegistrationRequirements && (
                <div className="general__registration-requirements">
                  <TextInput
                    label="PayFast Merchant ID:"
                    name="pay-fast-merchant-id"
                    value={payfastMerchantId}
                    onChange={({ target }) => setPayfastMerchantId(target.value)}
                    disabled={isArchivedProgramme}
                  />
                  <TextInput
                    label="PayFast Merchant Key:"
                    name="pay-fast-merchant-key"
                    value={payfastMerchantKey}
                    onChange={({ target }) => setPayfastMerchantKey(target.value)}
                    disabled={isArchivedProgramme}
                  />
                  {isCreatingSubProgrammes ? (
                    <div className="general__registration-requirements__sub-programme-prices">
                      <p>Programme Price</p>
                      {subProgrammes
                        .filter((subProgramme) => subProgramme.name)
                        .map((subProgramme) => (
                          <TextInput
                            key={subProgramme.id}
                            label={`${subProgramme.name}:`}
                            name={`price-${subProgramme.id}`}
                            value={subProgramme.price}
                            onChange={({ target }) => handleSubProgrammePriceChange(target.value, subProgramme.id)}
                            disabled={isArchivedProgramme}
                          />
                        ))}
                    </div>
                  ) : (
                    <TextInput
                      label="Programme Price:"
                      name="programme-price"
                      value={programmePrice}
                      onChange={({ target }) => setProgrammePrice(target.value)}
                      disabled={isArchivedProgramme}
                    />
                  )}

                  <div className="general__registration-requirements__required-fields">
                    <p className="general__registration-requirements__required-fields__label">Required Fields:</p>
                    <div className="general__registration-requirements__required-fields__fields">
                      {REQUIRED_REGISTRATION_FIELDS.map((field) => (
                        <p
                          className="general__registration-requirements__required-fields__default-field"
                          key={field.label}
                        >
                          {field.label}
                        </p>
                      ))}
                      {(programmeFields as Field[])?.map((field, index) => (
                        <div className="general__registration-requirements__required-fields__field" key={index}>
                          <TextInput
                            name={`field-${index}`}
                            value={field.label}
                            onChange={({ target }) => handleRequiredFieldLabelChange(target.value, index)}
                            disabled={isArchivedProgramme}
                          />

                          <div className="general__registration-requirements__required-fields__field__checkbox-container">
                            <Checkbox
                              label="Optional?"
                              name={`optional-${index}`}
                              value={field.optional}
                              onChange={(value) => handleRequiredFieldOptionalChange(value, index)}
                              disabled={isArchivedProgramme}
                            />
                          </div>

                          <div className="general__registration-requirements__required-fields__field__checkbox-container">
                            <Checkbox
                              label="Checkbox?"
                              name={`type-${index}`}
                              value={field.type === 'checkbox'}
                              onChange={(value) => handleRequiredFieldCheckboxChange(value, index)}
                              disabled={isArchivedProgramme}
                            />
                          </div>

                          {!isArchivedProgramme && (
                            <CloseIcon
                              className="general__registration-requirements__required-fields__field__delete-icon"
                              onClick={() => handleDeleteRequiredFieldClick(index)}
                              data-testid="delete-required-field-button"
                            />
                          )}
                        </div>
                      ))}
                      {!isArchivedProgramme && <Button text="Add" onClick={handleAddRequiredFieldClick} />}
                      <span ref={scrollRef} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {(isUpcomingProgramme || isNewProgramme || (requiresSettingsToggle && isSettingsOpen)) && renderEmailEditor()}

          {isUpcomingProgramme && (
            <>
              {renderToggle(true)}
              {renderTable()}
            </>
          )}

          {requiresSettingsToggle && !isSettingsOpen && renderTable()}
        </div>
      </div>

      <Modal show={showArchiveModal}>
        <div className="general__archive-modal" data-testid="member-modal">
          <div className="general__archive-modal__header">Archive Programme</div>
          <p className="general__archive-modal__message">Are you sure you want to archive this programme?</p>
          <div className="general__archive-modal__button-container">
            <Button text="Cancel" onClick={() => setShowArchiveModal(false)} />
            <Button text="Archive" onClick={handleArchiveConfirmationClick} />
          </div>
        </div>
      </Modal>

      <SaveChangesModal
        show={!!(unsavedSubProgramme || redirectUrl)}
        redirectUrl={redirectUrl}
        onSaveClick={() => handleSaveClick(changedPartsRef.current ?? [])}
        onDiscardClick={handleDiscardUnsavedChangesClick}
      />
    </ProgrammeLayout>
  );
};

export default General;
